import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { getMediumThumbnailLoadingImage } from '../util/thumbnailLoadingImages';

type ProgressiveImageProps = {
  src?: string | null;
  alt: string;
  seed?: string | number | null;
  fallbackSrc?: string;
  loading?: 'lazy' | 'eager';
  sx?: SxProps<Theme>;
  imageSx?: SxProps<Theme>;
  visibleOpacity?: number;
  onLoad?: () => void;
  onError?: () => void;
  onLoadStart?: () => void;
};

const sxArray = (sx?: SxProps<Theme>) => (
  Array.isArray(sx) ? sx : sx ? [sx] : []
);

const ProgressiveImage: React.FC<ProgressiveImageProps> = ({
  src,
  alt,
  seed,
  fallbackSrc,
  loading = 'lazy',
  sx,
  imageSx,
  visibleOpacity = 1,
  onLoad,
  onError,
  onLoadStart,
}) => {
  const resolvedSrc = src || '';
  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);
  const onLoadStartRef = useRef(onLoadStart);
  const fallback = useMemo(
    () => fallbackSrc || getMediumThumbnailLoadingImage(seed || resolvedSrc || alt),
    [alt, fallbackSrc, resolvedSrc, seed],
  );
  const [failed, setFailed] = useState(false);
  const [loadedSrc, setLoadedSrc] = useState('');

  useEffect(() => {
    onLoadRef.current = onLoad;
    onErrorRef.current = onError;
    onLoadStartRef.current = onLoadStart;
  }, [onError, onLoad, onLoadStart]);

  useEffect(() => {
    setFailed(false);
    setLoadedSrc('');
    if (resolvedSrc) {
      onLoadStartRef.current?.();
    }
  }, [resolvedSrc]);

  const realImageVisible = Boolean(
    resolvedSrc &&
    !failed &&
    loadedSrc === resolvedSrc,
  );

  return (
    <Box
      sx={[
        {
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#1E2230',
        },
        ...sxArray(sx),
      ]}
    >
      <Box
        component="img"
        src={fallback}
        alt=""
        aria-hidden="true"
        sx={[
          {
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: realImageVisible ? 0 : 1,
            transition: 'opacity 180ms ease',
          },
          ...sxArray(imageSx),
        ]}
      />
      {resolvedSrc && !failed && (
        <Box
          component="img"
          src={resolvedSrc}
          alt={alt}
          loading={loading}
          onLoad={() => {
            setLoadedSrc(resolvedSrc);
            onLoadRef.current?.();
          }}
          onError={() => {
            setFailed(true);
            setLoadedSrc('');
            onErrorRef.current?.();
          }}
          sx={[
          {
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'opacity 180ms ease',
          },
          ...sxArray(imageSx),
          {
            opacity: realImageVisible ? visibleOpacity : 0,
          },
        ]}
      />
      )}
    </Box>
  );
};

export default ProgressiveImage;
